import { useEffect, useState } from 'react';
import { CheckCircle } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { evalResultsApi, type HumanVerdict } from '../store/eval-results-api';

const { usePatchV2AdminCortexEvalResultsByResultIdReviewMutation: useReviewEvalResult } =
  evalResultsApi;

interface ReviewVerdictFormProps {
  resultId: string;
  initialVerdict?: HumanVerdict | null;
  initialNotes?: string | null;
  alreadyReviewed?: boolean;
  onSaved?: (verdict: HumanVerdict, notes: string) => void;
}

const VERDICT_OPTIONS: { value: HumanVerdict; label: string }[] = [
  { value: 'agree', label: 'Agree' },
  { value: 'disagree', label: 'Disagree' },
  { value: 'unclear', label: 'Unclear' },
];

export function ReviewVerdictForm({
  resultId,
  initialVerdict,
  initialNotes,
  alreadyReviewed = false,
  onSaved,
}: ReviewVerdictFormProps) {
  const [verdict, setVerdict] = useState<HumanVerdict | ''>(initialVerdict ?? '');
  const [notes, setNotes] = useState<string>(initialNotes ?? '');
  const [savedFlash, setSavedFlash] = useState(false);

  const [submitReview, { isLoading, error, reset }] = useReviewEvalResult();

  useEffect(() => {
    setVerdict(initialVerdict ?? '');
    setNotes(initialNotes ?? '');
  }, [resultId, initialVerdict, initialNotes]);

  const handleSubmit = async () => {
    if (!verdict) return;
    reset();
    try {
      await submitReview({
        resultId,
        reviewRequest: {
          human_verdict: verdict,
          human_notes: notes.trim() ? notes.trim() : null,
        },
      }).unwrap();
      setSavedFlash(true);
      onSaved?.(verdict, notes);
      window.setTimeout(() => setSavedFlash(false), 2500);
    } catch {
      // RTK Query stores the error in the hook state; surfaced below.
    }
  };

  const errorMessage =
    error && 'data' in error
      ? (error.data as { detail?: string } | undefined)?.detail || 'Failed to save review'
      : error
        ? 'Failed to save review'
        : null;

  return (
    <Box className="bg-white rounded-lg shadow p-6">
      <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
        SME Review
      </Typography>

      {alreadyReviewed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This result has already been reviewed. Saving will overwrite the previous verdict.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="verdict-label">Verdict</InputLabel>
          <Select
            labelId="verdict-label"
            label="Verdict"
            value={verdict}
            onChange={e => setVerdict(e.target.value)}
            disabled={isLoading}
          >
            {VERDICT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Notes"
          multiline
          minRows={3}
          maxRows={10}
          fullWidth
          value={notes}
          onChange={e => setNotes(e.target.value)}
          disabled={isLoading}
          placeholder="Optional reviewer notes (no PHI)"
        />

        {errorMessage && (
          <Alert severity="error" onClose={() => reset()}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={isLoading || !verdict}
          >
            {isLoading ? 'Saving…' : 'Save Review'}
          </Button>
          {savedFlash && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
              <CheckCircle fontSize="small" />
              <Typography variant="body2">Saved</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
